import type { DictionaryEntryView, DictionarySummary } from './types'

/**
 * Folding a background dictionary re-fetch back into the upload modal's picker.
 *
 * The user keeps interacting while those requests are in flight — checking a
 * dictionary, saving a hint word into a new one — so every helper here is a
 * `previous -> next` reducer meant to be handed straight to a functional
 * `setState`. None of them may resurrect what the user just did or drop what
 * they just added, which is why removals are expressed as an explicit
 * {@link removedDictionaryIds} set computed from the state the refresh STARTED
 * from: anything that appeared afterwards was created locally and is not
 * "missing from the server", it simply postdates the response.
 *
 * Each helper returns its `previous` argument unchanged when nothing moved, so
 * a tab switch that found no edits re-renders nothing — a new `dictionaries`
 * identity alone would cascade into the hint expansion and reshuffle the
 * suggestion row.
 */

/**
 * The dictionaries that are genuinely gone: present when the refresh started,
 * absent from what it fetched. Everything else the picker holds is either in
 * the response or newer than it.
 */
export function removedDictionaryIds(
  knownAtStart: DictionarySummary[],
  fetched: DictionarySummary[]
): Set<number> {
  const fetchedIds = new Set(fetched.map((dictionary) => dictionary.id))
  const removed = new Set<number>()
  for (const dictionary of knownAtStart) {
    if (!fetchedIds.has(dictionary.id)) removed.add(dictionary.id)
  }
  return removed
}

function summariesEqual(a: DictionarySummary, b: DictionarySummary): boolean {
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.description === b.description &&
    a.entryCount === b.entryCount &&
    a.createdAt === b.createdAt &&
    a.updatedAt === b.updatedAt
  )
}

/**
 * The fetched list, with any dictionary created while the request was in flight
 * kept in front of it (that is where `handleSaveTerm` puts a brand-new one).
 */
export function mergeDictionaryList(
  previous: DictionarySummary[],
  fetched: DictionarySummary[],
  removed: ReadonlySet<number>
): DictionarySummary[] {
  const fetchedIds = new Set(fetched.map((dictionary) => dictionary.id))
  const localOnly = previous.filter(
    (dictionary) => !fetchedIds.has(dictionary.id) && !removed.has(dictionary.id)
  )
  const next = localOnly.length > 0 ? [...localOnly, ...fetched] : fetched
  const unchanged =
    next.length === previous.length &&
    next.every((dictionary, index) => summariesEqual(dictionary, previous[index]))
  return unchanged ? previous : next
}

/** Drop only the checked dictionaries that no longer exist; keep the order. */
export function pruneSelectedIds(previous: number[], removed: ReadonlySet<number>): number[] {
  if (!previous.some((id) => removed.has(id))) return previous
  return previous.filter((id) => !removed.has(id))
}

/**
 * How one checked dictionary's terms stand right now.
 *
 * There is deliberately no "reloading" state and no gate around a re-read. An
 * earlier revision made Start wait for every catch-up; that gate produced a
 * fresh way to disable Start permanently in each of three review rounds
 * (a hung request, a timer consumed by an unrelated read, a release that freed
 * someone else's read), to buy a guard over a window of a few hundred
 * milliseconds. A re-read now updates in place, silently: the worst case is a
 * batch submitted in that window carrying the terms already on screen — which
 * is what the modal did before any of this, and the next catch-up corrects it.
 */
export type DictionaryEntryState =
  | { status: 'loading' }
  | { status: 'loaded'; entries: DictionaryEntryView[] }
  | { status: 'failed' }

/** The terms this state can contribute to the upload, if any. */
export function entriesOfState(state: DictionaryEntryState | undefined): DictionaryEntryView[] {
  return state?.status === 'loaded' ? state.entries : []
}

/**
 * Whether these terms can be uploaded as they stand. Start waits for anything
 * else: a first read still running has nothing to send, and a failed read
 * leaves the user believing terms ride along that would silently be dropped —
 * that group offers retry and 這次不用這本 as the two ways to unblock.
 */
export function entryStateIsUsable(state: DictionaryEntryState | undefined): boolean {
  return state?.status === 'loaded'
}
